import { addEntity } from '@/backend/addEntity'
import { confirmFieldFileUpload } from '@/backend/confirmFieldFileUpload'
import { deleteEntity } from '@/backend/deleteEntity'
import { startFieldFileUpload } from '@/backend/startFieldFileUpload'
import { uploadFile } from '@/backend/uploadFile'
import { getOptimisticHubEntity } from '@/data/Hubs/optimisticData'
import type { Hub } from '@/data/Hubs/types'
import { useEntityStore } from '@/data/useEntityStore'
import { serializeEntity } from '@/modules/Project/serializer'
import type { Entity } from '@/modules/Project/types'
import type { Project } from '@/modules/Projects/useProjects'
import { promiseQueue } from '@/modules/Workspaces/KnowledgeHub/Files/promiseQueue'
import type { FileAndPath } from '@/shared/utils/file'
import { invariant } from '@/shared/utils/typeAssertions'

export async function uploadHubFiles(props: {
  hub: Hub
  fileProject: Project
  files: FileAndPath[]
}) {
  const entityStore = useEntityStore()
  const promises: Array<() => Promise<void>> = []

  setOptimisticHubProcessingStatus(props.hub)

  for (const file of props.files) {
    const optimisticEntity = getOptimisticHubEntity({
      hubProject: props.hub,
      fileProject: props.fileProject,
      path: file.fullPath,
    })
    entityStore.pushEntity(optimisticEntity)

    promises.push(() => {
      return uploadHubFile({
        hub: props.hub,
        file,
        fileProject: props.fileProject,
        onEntityCreated() {
          entityStore.removeEntities([optimisticEntity.id])
        },
      })
    })
  }

  await promiseQueue(promises, { maxConcurrent: 20 })
}

async function uploadHubFile(props: {
  hub: Hub
  fileProject: Project
  file: FileAndPath
  onEntityCreated?: (entity: Entity) => void
}) {
  const entityStore = useEntityStore()
  const { hub, file } = props

  const entityResponse = await addEntity({
    workspaceId: hub.workspaceId,
    projectId: hub.fileProjectId,
    parentEntityId: hub.entityId,
    fields: { path: file.fullPath },
  })

  invariant(entityResponse.ok, 'Failed to create entity')
  const entity = serializeEntity(entityResponse.data)
  entityStore.pushEntity(entity)
  props.onEntityCreated?.(entity)

  try {
    const uploadUrlResult = await startFieldFileUpload(
      hub.workspaceId,
      hub.fileProjectId,
      entity.id,
      'file',
      file.file.name,
    )

    invariant(uploadUrlResult.ok, 'Failed to start file upload')
    const uploadUrl = uploadUrlResult.data.file_upload_url

    const uploadResponse = await uploadFile(uploadUrl, file.file)
    invariant(uploadResponse.ok, 'Failed to upload file')

    const confirmation = await confirmFieldFileUpload(uploadUrlResult.data.confirm_upload_url)
    invariant(confirmation.ok)
  } catch {
    await deleteEntity({
      workspaceId: hub.workspaceId,
      projectId: hub.fileProjectId,
      entityId: entity.id,
    })
  }
}

function setOptimisticHubProcessingStatus(hub: Hub) {
  if (!hub.entityId || !hub.index) return

  const entityStore = useEntityStore()
  const entity = entityStore.getFullEntity(hub.entityId)
  if (!entity) return

  const indexField = entity.fields.get(hub.index.propertyId)
  if (!indexField) return

  indexField.status = 'uploading'
}
